import { z } from "zod";

/**
 * Encuestas al personal (2026-11-02). Una encuesta nace en borrador, se publica a una lista de
 * personas y se cierra a mano o al llegar su fecha. Al publicarse queda congelada: las respuestas
 * se guardan contra sus preguntas y no se puede cambiar lo que ya se contestó.
 */

export const TIPOS_DE_PREGUNTA = ["unica", "multiple", "escala", "texto"] as const;
export type TipoDePregunta = (typeof TIPOS_DE_PREGUNTA)[number];

export const NOMBRE_TIPO_DE_PREGUNTA: Record<TipoDePregunta, string> = {
  unica: "Una opción",
  multiple: "Varias opciones",
  escala: "Escala del 1 al 5",
  texto: "Texto libre",
};

export const ESTADOS_DE_ENCUESTA = ["borrador", "abierta", "cerrada"] as const;
export type EstadoDeEncuesta = (typeof ESTADOS_DE_ENCUESTA)[number];

/**
 * Una encuesta anónima no muestra resultados con menos respuestas que estas: con una o dos, quien
 * sabe quién contestó sabe también qué contestó.
 */
export const MINIMO_ANONIMAS = 3;

const MAXIMO_PREGUNTAS = 40;
const MAXIMO_OPCIONES = 20;
const MAXIMO_TEXTO = 2000;
export const VALORES_DE_ESCALA = [1, 2, 3, 4, 5] as const;

/** Las preguntas con opciones: las demás no llevan ninguna. */
export const conOpciones = (tipo: TipoDePregunta) => tipo === "unica" || tipo === "multiple";

const esquemaPregunta = z.object({
  id: z.string().min(1).max(40),
  tipo: z.enum(TIPOS_DE_PREGUNTA),
  texto: z.string().trim().max(500, "Una pregunta no puede pasar de 500 caracteres"),
  obligatoria: z.boolean(),
  opciones: z
    .array(z.string().trim().max(200, "Una opción no puede pasar de 200 caracteres"))
    .max(MAXIMO_OPCIONES, `Una pregunta no puede tener más de ${MAXIMO_OPCIONES} opciones`),
});
export type Pregunta = z.infer<typeof esquemaPregunta>;

/** Un borrador se guarda como vaya: lo que debe estar completo se exige al publicar. */
export const esquemaGuardarEncuesta = z.object({
  id: z.uuid().optional(),
  titulo: z
    .string()
    .trim()
    .min(3, "Ponle un título a la encuesta")
    .max(200, "El título no puede pasar de 200 caracteres"),
  descripcion: z.string().trim().max(MAXIMO_TEXTO),
  anonima: z.boolean(),
  paraTodos: z.boolean(),
  destinatarios: z.array(z.string().min(1)).max(2000),
  cierraEn: z.date().nullable(),
  preguntas: z
    .array(esquemaPregunta)
    .max(MAXIMO_PREGUNTAS, `Una encuesta no puede tener más de ${MAXIMO_PREGUNTAS} preguntas`),
});
export type GuardarEncuesta = z.infer<typeof esquemaGuardarEncuesta>;

/** Lo que le falta a un borrador para publicarse. Vacío = se puede publicar. */
export function problemasDeEncuesta(
  encuesta: Pick<GuardarEncuesta, "paraTodos" | "destinatarios" | "cierraEn" | "preguntas">,
  ahora = new Date(),
): string[] {
  const problemas: string[] = [];
  if (encuesta.preguntas.length === 0) problemas.push("Agrega al menos una pregunta");
  encuesta.preguntas.forEach((p, i) => {
    const n = `Pregunta ${i + 1}`;
    if (!p.texto) problemas.push(`${n}: escribe la pregunta`);
    if (!conOpciones(p.tipo)) return;
    if (p.opciones.length < 2) problemas.push(`${n}: necesita al menos dos opciones`);
    if (p.opciones.some((o) => !o)) problemas.push(`${n}: hay una opción vacía`);
    if (new Set(p.opciones).size !== p.opciones.length) {
      problemas.push(`${n}: hay opciones repetidas`);
    }
  });
  if (!encuesta.paraTodos && encuesta.destinatarios.length === 0) {
    problemas.push("Elige a quién va la encuesta");
  }
  if (encuesta.cierraEn && encuesta.cierraEn <= ahora) {
    problemas.push("La fecha de cierre ya pasó");
  }
  return problemas;
}

/** Lo contestado a una pregunta; la que se deja en blanco no aparece. */
type Respuesta = string | string[] | number;
export type Respuestas = Record<string, Respuesta>;

export const esquemaResponderEncuesta = z.object({
  id: z.uuid(),
  respuestas: z.record(
    z.string(),
    z.union([z.string().max(MAXIMO_TEXTO), z.array(z.string().max(200)).max(50), z.number()]),
  ),
});

const enBlanco = (r: Respuesta | undefined) =>
  r === undefined || r === "" || (Array.isArray(r) && r.length === 0);

/**
 * Comprueba lo contestado contra las preguntas y devuelve solo lo que corresponde a ellas, sin
 * espacios de más. Devuelve el texto del problema si algo no cuadra.
 */
export function validarRespuestas(
  preguntas: Pregunta[],
  crudas: Respuestas,
): { respuestas: Respuestas } | { problema: string } {
  const respuestas: Respuestas = {};
  for (const [i, p] of preguntas.entries()) {
    const n = `Pregunta ${i + 1}`;
    const cruda = crudas[p.id];
    const valor = typeof cruda === "string" ? cruda.trim() : cruda;
    if (valor === undefined || enBlanco(valor)) {
      if (p.obligatoria) return { problema: `${n}: esta pregunta es obligatoria` };
      continue;
    }
    if (p.tipo === "texto" && typeof valor !== "string") return { problema: `${n}: no es válida` };
    if (p.tipo === "unica" && (typeof valor !== "string" || !p.opciones.includes(valor))) {
      return { problema: `${n}: esa opción no existe` };
    }
    if (p.tipo === "multiple") {
      const validas = Array.isArray(valor) && valor.every((v) => p.opciones.includes(v));
      if (!validas || new Set(valor).size !== valor.length) {
        return { problema: `${n}: esas opciones no existen` };
      }
    }
    if (p.tipo === "escala" && !VALORES_DE_ESCALA.some((v) => v === valor)) {
      return { problema: `${n}: elige un valor del 1 al 5` };
    }
    respuestas[p.id] = valor;
  }
  return { respuestas };
}

export type ResumenDePregunta = {
  id: string;
  tipo: TipoDePregunta;
  texto: string;
  /** Cuántas personas contestaron esta pregunta. */
  contestaron: number;
  /** Conteo por opción, o por valor en una escala; vacío en una de texto. */
  conteos: { etiqueta: string; votos: number }[];
  /** Solo en una escala; null si nadie la contestó. */
  promedio: number | null;
  /** Solo en una de texto. */
  textos: string[];
};

/** Lo que muestran la pantalla de resultados y las exportaciones. */
export function resumirRespuestas(preguntas: Pregunta[], filas: Respuestas[]): ResumenDePregunta[] {
  return preguntas.map((p) => {
    const dadas = filas.map((f) => f[p.id]).filter((r) => !enBlanco(r));
    const etiquetas =
      p.tipo === "escala" ? VALORES_DE_ESCALA.map(String) : conOpciones(p.tipo) ? p.opciones : [];
    const elegidas = dadas.flatMap((r) => (Array.isArray(r) ? r : [String(r)]));
    const numeros = p.tipo === "escala" ? dadas.map(Number) : [];
    return {
      id: p.id,
      tipo: p.tipo,
      texto: p.texto,
      contestaron: dadas.length,
      conteos: etiquetas.map((etiqueta) => ({
        etiqueta,
        votos: elegidas.filter((e) => e === etiqueta).length,
      })),
      promedio: numeros.length ? numeros.reduce((s, v) => s + v, 0) / numeros.length : null,
      textos: p.tipo === "texto" ? dadas.map(String) : [],
    };
  });
}

/** Una respuesta como se lee en una celda de la exportación. */
export function textoDeRespuesta(respuesta: Respuesta | undefined): string {
  if (respuesta === undefined) return "";
  return Array.isArray(respuesta) ? respuesta.join(", ") : String(respuesta);
}

/** Una encuesta abierta deja de recibir respuestas al llegar su fecha, sin que nadie la cierre. */
export function encuestaVencida(
  encuesta: { estado: string; cierraEn: Date | null },
  ahora = new Date(),
): boolean {
  return (
    encuesta.estado === "cerrada" ||
    (encuesta.estado === "abierta" && !!encuesta.cierraEn && encuesta.cierraEn <= ahora)
  );
}
